// ============================================================
// ACCOMMODATION PAYLOAD - FRONTEND FORM DATA -> BACKEND SHAPE
//
// Used on submit (create + update). The reverse direction
// (backend record -> form data, used to populate the edit
// form) lives in ./fromBackend.js.
// ============================================================

import { isObject, cleanString, booleanValue, stringArray, createSlug } from './helpers';
import { transformAddress } from './address';
import { transformAmenities } from './amenities';
import { transformServices } from './services';
import { transformUnit } from './units';
import { transformPolicies, transformBookingSettings } from './policies';
import { transformSeasonalPricing } from './pricing';
import { transformMedia, transformCardImages, transformPaymentOptions } from './media';
import { transformNearbyPlaces, transformFaqs } from './additional';
import { transformHouseRules } from './policies';

export function buildAccommodationPayload(formData = {}) {
    const rawUnits = Array.isArray(formData.units) ? formData.units : [];

    const units = rawUnits.map(transformUnit);

    /*
     * Calculate property capacity from units.
     */
    const maxGuests = units.reduce(
        (total, unit) => total + Number(unit?.maximumGuests || unit?.occupancy?.total || 0),
        0
    );

    const totalBedrooms = units.reduce((total, unit) => total + Number(unit?.bedrooms || 0), 0);

    const totalBathrooms = units.reduce((total, unit) => total + Number(unit?.bathrooms?.count || 0), 0);

    const totalUnits = units.reduce((total, unit) => total + Number(unit?.inventory?.totalUnits || 0), 0);

    const additional = formData.additional || {};

    const policies = transformPolicies(formData.policies || {});

    /*
     * Booking rules are edited in the Policies step; bookingSettings is what
     * search and the booking tab read (same-day check-in, advance days), so
     * take those from the Policies step instead of the untouched defaults.
     */
    const bookingSettings = transformBookingSettings({
        ...(formData.bookingSettings || {}),
        instantBooking: policies.instantBooking,
        sameDayBooking: policies.sameDayBooking,
        advanceBookingDays: policies.advanceBookingDays
    });

    const title = cleanString(formData.title);

    const slug = cleanString(formData.slug) || createSlug(title);

    const payload = {
        /* ========================================================
       LISTING
    ======================================================== */

        title,

        overview: cleanString(formData.overview || formData.shortDescription || formData.description),

        description: cleanString(formData.description),

        slug,

        /* ========================================================
       LOCATION
    ======================================================== */

        address: transformAddress(formData.address),

        /* ========================================================
       MEDIA
    ======================================================== */

        media: transformMedia(formData.media || formData.images || []),

        cardImages: transformCardImages(formData.cardImages),

        videos: stringArray(formData.videos),

        /* ========================================================
       PROPERTY
    ======================================================== */

        propertyType: cleanString(formData.propertyType),

        // blank isn't an allowed value — leave it out instead
        propertyCategory: cleanString(formData.propertyCategory) || undefined,

        maxGuests: Math.max(Number(maxGuests), 1),

        entirePropertyAvailable: booleanValue(formData.entirePropertyAvailable, false),

        totalBedrooms,

        totalBathrooms,

        totalUnits: totalUnits || units.length || 1,

        /* ========================================================
       UNITS
    ======================================================== */

        units,

        /* ========================================================
       AMENITIES
    ======================================================== */

        amenities: transformAmenities(formData.amenities),

        /* ========================================================
       SERVICES
       (fixed: formData.services is grouped by category, e.g.
       { food: [...], transport: [...] } - transformServices now
       flattens that before mapping, instead of silently
       returning [] for a non-array input.)
    ======================================================== */

        services: transformServices(formData.services),

        /* ========================================================
       HOUSE / PROPERTY RULES
    ======================================================== */

        propertyRules: transformHouseRules(formData.propertyRules || formData.policies?.houseRules || {}),

        /* ========================================================
       CHECK-IN
    ======================================================== */

        // what the Additional info step shows (formData.checkInInstructions is
        // only the copy loaded on edit)
        checkInInstructions: {
            checkInTime: cleanString(
                additional.checkInFrom || formData.checkInInstructions?.checkInTime || policies.checkInTime
            ),

            checkOutTime: cleanString(
                additional.checkOutFrom || formData.checkInInstructions?.checkOutTime || policies.checkOutTime
            ),

            instructions: cleanString(additional.checkInInstructions ?? formData.checkInInstructions?.instructions)
        },

        // the other Additional info fields
        additionalInfo: {
            checkInUntil: cleanString(additional.checkInUntil),
            checkOutUntil: cleanString(additional.checkOutUntil),
            selfCheckIn: booleanValue(additional.selfCheckIn, false),
            guestInformation: cleanString(additional.guestInformation),
            parkingType: cleanString(additional.parkingType),
            parkingCapacity: Number(additional.parkingCapacity) || 0,
            transportationInfo: cleanString(additional.transportationInfo),
            notes: cleanString(additional.notes)
        },

        /* ========================================================
       ACCESSIBILITY
    ======================================================== */

        // ticked boxes in Additional info -> list of keys
        accessibility: isObject(additional.accessibility)
            ? Object.keys(additional.accessibility).filter((key) => additional.accessibility[key])
            : stringArray(formData.accessibility),

        /* ========================================================
       NEARBY
    ======================================================== */

        nearbyPlaces: transformNearbyPlaces(formData.nearbyPlaces || additional.nearbyPlaces),

        /* ========================================================
       SEASONAL PRICING
    ======================================================== */

        seasonalPricing: transformSeasonalPricing(formData.seasonalPricing),

        /* ========================================================
       PAYMENT
    ======================================================== */

        paymentOptions: transformPaymentOptions(formData.paymentOptions || {}),

        /* ========================================================
       HIGHLIGHTS
    ======================================================== */

        propertyHighlights: stringArray(
            formData.propertyHighlights || formData.highlights || additional.propertyHighlights
        ),

        /* ========================================================
       LANGUAGES
    ======================================================== */

        languagesSpoken: stringArray(formData.languagesSpoken || formData.languages),

        /* ========================================================
       OPENING HOURS
    ======================================================== */

        openingHours: isObject(formData.openingHours) ? formData.openingHours : {},

        /* ========================================================
       COMMON LISTING DATA
    ======================================================== */

        tags: stringArray(formData.tags),

        inclusions: stringArray(formData.inclusions || formData.included),

        exclusions: stringArray(formData.exclusions || formData.excluded),

        thingsToCarry: stringArray(formData.thingsToCarry || formData.carry),

        safetyMeasures: stringArray(formData.safetyMeasures || formData.safety),

        terms: stringArray(formData.terms),

        faqs: transformFaqs(formData.faqs || additional.faqs),

        /* ========================================================
       POLICIES
    ======================================================== */

        policies,

        /* ========================================================
       BOOKING SETTINGS
    ======================================================== */

        bookingSettings,

        /* ========================================================
       SEO
    ======================================================== */

        seo: isObject(formData.seo) ? formData.seo : {}
    };

    return payload;
}

export default buildAccommodationPayload;
